"use client";

import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import {
	AnimatePresence,
	domMax,
	LazyMotion,
	m,
	type Transition,
} from "motion/react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { Icon } from "../icon";
import { Ripple, useRippleState } from "../ripple";
import { SPRING_TRANSITION_EXPRESSIVE } from "../shared/constants";
import { TouchTarget } from "../shared/touch-target";
import {
	getNavigationShapeStyle,
	isSquareShape,
	type NavigationItemShape,
} from "./navigation-shape-utils";

export type { NavigationItemShape };

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Layout styling for navigation bar items.
 * - vertical: Icon above label (default for mobile)
 * - horizontal: Icon beside label (forced)
 */
export type NavigationBarItemLayout = "vertical" | "horizontal";

/**
 * Visual variant of the Navigation Bar.
 * - flexible: Default MD3 behavior (h-16), becomes horizontal on desktop.
 * - baseline: Taller MD3 behavior (h-20), always vertical.
 * - xr: Floating orbiter variant for spatial interfaces (detached from bottom).
 */
export type NavigationBarVariant = "flexible" | "baseline" | "xr";

export type NavigationBarLabelVisibility = "labeled" | "auto" | "unlabeled";

export interface NavigationBarItemProps {
	selected: boolean;
	icon: React.ReactNode;
	label?: React.ReactNode;
	onClick?: () => void;
	disabled?: boolean;
	badge?: React.ReactNode;
	/** Shape of the item active indicator / icon container (pill, circle, sunny, flower, etc.) */
	shape?: NavigationItemShape;
	/** Override size (width & height) of the shape container in px. Default: 56 for square shapes, 32h×56w for pill */
	shapeSize?: number;
	/** Hide the label below the icon. Overrides container-level labelVisibility. */
	hideLabel?: boolean;
	"aria-label"?: string;
	className?: string;
	asChild?: boolean;
	children?: React.ReactNode;
}

export interface NavigationBarProps {
	/** Visual variant of the Navigation Bar */
	variant?: NavigationBarVariant;
	/** Forces a specific item layout (horizontal/vertical) */
	itemLayout?: NavigationBarItemLayout;
	/** Default shape of navigation items (pill, circle, sunny, flower, etc.) */
	shape?: NavigationItemShape;
	/**
	 * Label visibility for all items.
	 * - "labeled" (default): always show label
	 * - "auto": show label only for the selected item
	 * - "unlabeled": never show label (icon-only mode)
	 */
	labelVisibility?: NavigationBarLabelVisibility;
	/** Default size (width & height) for square shape containers in px. Default: 56 */
	shapeSize?: number;
	/** Whether the bar should hide when scrolling down */
	hideOnScroll?: boolean;
	/** Whether the bar should have an elevation shadow */
	elevated?: boolean;
	/** Whether the bar is fixed to the viewport (default) or absolute */
	fixed?: boolean;
	/** Container ref to track scrolling for hideOnScroll */
	scrollContainerRef?: React.RefObject<HTMLElement | null>;
	/** Transition for the active indicator pill */
	activeIndicatorTransition?: Transition;
	/** Navigation items */
	children: React.ReactNode;
	/** Optional additional classes for individual navigation items */
	itemClassName?: string;
	/** Optional additional classes */
	className?: string;
	/** Optional inline styles */
	style?: React.CSSProperties;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const NavigationBarContext = React.createContext<{
	variant: NavigationBarVariant;
	itemLayout?: NavigationBarItemLayout;
	shape?: NavigationItemShape;
	labelVisibility?: NavigationBarLabelVisibility;
	shapeSize?: number;
	activeIndicatorTransition?: Transition;
	itemClassName?: string;
}>({ variant: "flexible" });

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function cloneIconWithFill(
	icon: React.ReactNode,
	selected: boolean,
): React.ReactNode {
	if (!React.isValidElement(icon)) return icon;
	if ((icon.type as unknown) === Icon) {
		return React.cloneElement(
			icon as React.ReactElement<{ fill?: 0 | 1; animateFill?: boolean }>,
			{ fill: selected ? 1 : 0, animateFill: true },
		);
	}
	return icon;
}

// ─────────────────────────────────────────────────────────────────────────────
// NavigationBarItem Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface ActivePillProps {
	shape?: NavigationItemShape;
	width?: number;
	height?: number;
}

function ActivePill({
	shape = "pill",
	width = 56,
	height = 32,
}: ActivePillProps) {
	const { activeIndicatorTransition } = React.useContext(NavigationBarContext);
	const shapeStyle = getNavigationShapeStyle(shape, width, height);

	return (
		<m.div
			className={cn(
				"bg-m3-secondary-container pointer-events-none",
				shapeStyle
					? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-none"
					: shape === "circle"
						? "absolute inset-0 rounded-full"
						: "absolute inset-0 rounded-full",
			)}
			style={{
				zIndex: 0,
				...(shapeStyle ? { width, height } : {}),
				...shapeStyle,
			}}
			initial={{ opacity: 0, scale: 0.5 }}
			animate={{ opacity: 1, scale: 1 }}
			exit={{ opacity: 0, scale: 0.5 }}
			transition={activeIndicatorTransition || SPRING_TRANSITION_EXPRESSIVE}
		/>
	);
}

interface HoverStateLayerProps {
	shape?: NavigationItemShape;
	width?: number;
	height?: number;
}

function HoverStateLayer({
	shape = "pill",
	width = 56,
	height = 32,
}: HoverStateLayerProps) {
	const shapeStyle = getNavigationShapeStyle(shape, width, height);

	return (
		<div
			className={cn(
				"bg-m3-on-surface opacity-0 group-hover:opacity-[0.08] group-focus-visible:opacity-[0.10] active:opacity-[0.10] transition-opacity duration-200 pointer-events-none z-0",
				shapeStyle
					? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-none"
					: shape === "circle"
						? "absolute inset-0 rounded-full"
						: "absolute inset-0 rounded-[16px]",
			)}
			style={{
				...(shapeStyle ? { width, height } : {}),
				...shapeStyle,
			}}
		/>
	);
}

interface RippleLayerProps {
	ripples: ReturnType<typeof useRippleState>["ripples"];
	onRippleDone: ReturnType<typeof useRippleState>["removeRipple"];
	shape?: NavigationItemShape;
	width?: number;
	height?: number;
}

function RippleLayer({
	ripples,
	onRippleDone,
	shape = "pill",
	width = 56,
	height = 32,
}: RippleLayerProps) {
	const shapeStyle = getNavigationShapeStyle(shape, width, height);

	return (
		<div
			className={cn(
				"overflow-hidden pointer-events-none z-0",
				shapeStyle
					? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-none"
					: shape === "circle"
						? "absolute inset-0 rounded-full"
						: "absolute inset-0 rounded-[16px]",
			)}
			style={{
				...(shapeStyle ? { width, height } : {}),
				...shapeStyle,
			}}
		>
			<Ripple ripples={ripples} onRippleDone={onRippleDone} />
		</div>
	);
}

interface IconContainerProps {
	selected: boolean;
	badge?: React.ReactNode;
	children: React.ReactNode;
}

function IconContainer({ selected, badge, children }: IconContainerProps) {
	return (
		<div
			aria-hidden="true"
			className={cn(
				"relative flex items-center justify-center size-6 transition-colors duration-200 shrink-0",
				selected
					? "text-m3-on-secondary-container"
					: "text-m3-on-surface-variant",
			)}
		>
			{children}
			{badge && (
				<span className="absolute -top-1 -right-1 flex min-w-3 h-3 items-center justify-center rounded-full bg-m3-error px-1 text-[10px] font-medium leading-none tracking-normal text-m3-on-error ring-[1.5px] ring-m3-surface">
					{badge}
				</span>
			)}
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// NavigationBarItem
// ─────────────────────────────────────────────────────────────────────────────

const NavigationBarItemComponent = React.forwardRef<
	HTMLButtonElement,
	NavigationBarItemProps
>(
	(
		{
			selected,
			icon,
			label,
			onClick,
			disabled = false,
			badge,
			shape: shapeProp,
			shapeSize: shapeSizeProp,
			hideLabel = false,
			className,
			"aria-label": ariaLabelProp,
			asChild = false,
			children,
		},
		ref,
	) => {
		const {
			variant,
			itemLayout,
			shape: contextShape,
			labelVisibility: contextLabelVisibility,
			shapeSize: contextShapeSize,
			itemClassName: contextItemClassName,
		} = React.useContext(NavigationBarContext);

		const activeShape = shapeProp ?? contextShape ?? "pill";
		const isSquare = isSquareShape(activeShape);
		// shapeSize controls the square dimension of custom shape containers (default 56px)
		const resolvedShapeSize = shapeSizeProp ?? contextShapeSize ?? 56;

		const isForcedHorizontal = itemLayout === "horizontal";
		const isResponsiveHorizontal =
			(variant === "flexible" || variant === "xr") && itemLayout === undefined;

		// Label visibility: item-level hideLabel takes priority, then container labelVisibility
		const shouldShowLabel =
			!hideLabel &&
			(() => {
				if (contextLabelVisibility === "unlabeled") return false;
				if (contextLabelVisibility === "auto") return selected;
				return true; // "labeled" (default)
			})();

		const { ripples, onPointerDown, removeRipple } = useRippleState({
			disabled,
		});

		const handleClick = React.useCallback(
			(e: React.MouseEvent<HTMLButtonElement>) => {
				if (disabled) {
					e.preventDefault();
					return;
				}
				if (selected) {
					if (typeof window !== "undefined" && window.scrollY > 0) {
						window.scrollTo({ top: 0, behavior: "smooth" });
					}
					return;
				}
				onClick?.();
			},
			[disabled, selected, onClick],
		);

		const filledIcon = cloneIconWithFill(icon, selected);

		const itemClassName = cn(
			"group relative flex cursor-pointer transition-colors duration-200 outline-none select-none h-full items-center justify-center min-w-0",
			variant === "xr"
				? isResponsiveHorizontal
					? "flex-1 min-[600px]:flex-initial min-[600px]:w-auto"
					: isForcedHorizontal
						? "flex-initial w-auto"
						: "flex-1"
				: isForcedHorizontal
					? "flex-initial w-auto"
					: "flex-1",
			disabled && "pointer-events-none opacity-[0.38]",
			contextItemClassName,
			className,
		);

		// Icon pill container dimensions — square shapes use resolvedShapeSize × resolvedShapeSize
		const iconPillWidth = isSquare ? resolvedShapeSize : 56;
		const iconPillHeight = isSquare ? resolvedShapeSize : 32;

		if (asChild && children) {
			const child = React.Children.only(children) as React.ReactElement<{
				children?: React.ReactNode;
			}>;

			const innerContent = (
				<>
					<div
						className={cn(
							"relative flex items-center justify-center flex-col gap-y-1 w-full px-1 py-1.5",
							isResponsiveHorizontal &&
								"min-[600px]:flex-row min-[600px]:gap-y-0 min-[600px]:gap-x-1.5 min-[600px]:h-10 min-[600px]:px-4 min-[600px]:py-0 min-[600px]:rounded-full min-[600px]:w-auto",
							isForcedHorizontal &&
								"flex-row gap-y-0 gap-x-1.5 h-10 px-4 py-0 rounded-full w-auto",
							// When label is hidden and not horizontal, remove bottom gap
							!shouldShowLabel && !isForcedHorizontal && "gap-y-0",
						)}
					>
						{/* Horizontal active indicator — covers icon + label (only for standard pill layout) */}
						{!isSquare && (
							<div
								className={cn(
									"absolute inset-0 z-0 hidden",
									isResponsiveHorizontal && "min-[600px]:block",
									isForcedHorizontal && "block!",
								)}
							>
								<AnimatePresence initial={false}>
									{selected && (
										<ActivePill shape={activeShape} width={56} height={40} />
									)}
								</AnimatePresence>
								<HoverStateLayer shape={activeShape} width={56} height={40} />
								<RippleLayer
									ripples={ripples}
									onRippleDone={removeRipple}
									shape={activeShape}
									width={56}
									height={40}
								/>
							</div>
						)}

						{/* Icon pill — background visible for custom shapes always, or for pill in vertical layout */}
						<div
							className={cn(
								"relative flex items-center justify-center shrink-0 z-10",
								isSquare ? "mx-auto" : "h-8 w-14 mx-auto rounded-[16px]",
								!isSquare &&
									isResponsiveHorizontal &&
									"min-[600px]:size-6 min-[600px]:w-auto min-[600px]:h-auto",
								!isSquare && isForcedHorizontal && "size-6 w-auto h-auto",
							)}
							style={
								isSquare
									? { width: resolvedShapeSize, height: resolvedShapeSize }
									: undefined
							}
						>
							<div
								className={cn(
									"absolute inset-0 z-0",
									!isSquare && isResponsiveHorizontal && "min-[600px]:hidden",
									!isSquare && isForcedHorizontal && "hidden",
								)}
							>
								<AnimatePresence initial={false}>
									{selected && (
										<ActivePill
											shape={activeShape}
											width={iconPillWidth}
											height={iconPillHeight}
										/>
									)}
								</AnimatePresence>
								<HoverStateLayer
									shape={activeShape}
									width={iconPillWidth}
									height={iconPillHeight}
								/>
								<RippleLayer
									ripples={ripples}
									onRippleDone={removeRipple}
									shape={activeShape}
									width={iconPillWidth}
									height={iconPillHeight}
								/>
							</div>

							<div className="relative z-10 flex size-6 items-center justify-center text-current">
								<IconContainer selected={selected} badge={badge}>
									{filledIcon}
								</IconContainer>
							</div>
						</div>

						{label && (
							<AnimatePresence mode="popLayout" initial={false}>
								{shouldShowLabel && (
									<m.span
										key="nav-label"
										initial={{ opacity: 0, height: 0, scale: 0.85, y: -2 }}
										animate={{ opacity: 1, height: "auto", scale: 1, y: 0 }}
										exit={{ opacity: 0, height: 0, scale: 0.85, y: -2 }}
										transition={SPRING_TRANSITION_EXPRESSIVE}
										className={cn(
											"z-10 transition-colors duration-200 truncate px-1 inline-block overflow-hidden",
											selected
												? "text-m3-on-surface font-medium"
												: "text-m3-on-surface-variant font-medium",
											"text-[12px] leading-4 tracking-[0.5px]",
										)}
									>
										{child.props.children ?? label}
									</m.span>
								)}
							</AnimatePresence>
						)}
					</div>
					<TouchTarget />
				</>
			);

			return (
				<Slot
					ref={ref as React.Ref<HTMLElement>}
					role="menuitem"
					aria-current={selected ? "page" : undefined}
					aria-disabled={disabled ? true : undefined}
					aria-label={
						// Always provide aria-label even when label is visually hidden
						ariaLabelProp || (typeof label === "string" ? label : undefined)
					}
					onClick={handleClick}
					onPointerDown={onPointerDown}
					className={itemClassName}
				>
					{React.cloneElement(child, { children: innerContent })}
				</Slot>
			);
		}

		return (
			<m.button
				ref={ref}
				type="button"
				role="menuitem"
				aria-current={selected ? "page" : undefined}
				aria-disabled={disabled ? true : undefined}
				aria-label={
					// Always provide aria-label even when label is visually hidden
					ariaLabelProp || (typeof label === "string" ? label : undefined)
				}
				onClick={handleClick}
				onPointerDown={onPointerDown}
				className={itemClassName}
			>
				<div
					className={cn(
						"relative flex items-center justify-center flex-col gap-y-1 w-full px-1 py-1.5",
						isResponsiveHorizontal &&
							"min-[600px]:flex-row min-[600px]:gap-y-0 min-[600px]:gap-x-1.5 min-[600px]:h-10 min-[600px]:px-4 min-[600px]:py-0 min-[600px]:rounded-full min-[600px]:w-auto",
						isForcedHorizontal &&
							"flex-row gap-y-0 gap-x-1.5 h-10 px-4 py-0 rounded-full w-auto",
						// When label is hidden and not horizontal, remove bottom gap
						!shouldShowLabel && !isForcedHorizontal && "gap-y-0",
					)}
				>
					{/* Horizontal active indicator — covers icon + label (only for standard pill layout) */}
					{!isSquare && (
						<div
							className={cn(
								"absolute inset-0 z-0 hidden",
								isResponsiveHorizontal && "min-[600px]:block",
								isForcedHorizontal && "block!",
							)}
						>
							<AnimatePresence initial={false}>
								{selected && (
									<ActivePill shape={activeShape} width={56} height={40} />
								)}
							</AnimatePresence>
							<HoverStateLayer shape={activeShape} width={56} height={40} />
							<RippleLayer
								ripples={ripples}
								onRippleDone={removeRipple}
								shape={activeShape}
								width={56}
								height={40}
							/>
						</div>
					)}

					{/* Icon pill — background visible for custom shapes always, or for pill in vertical layout */}
					<div
						className={cn(
							"relative flex items-center justify-center shrink-0 z-10",
							isSquare ? "mx-auto" : "h-8 w-14 mx-auto rounded-[16px]",
							!isSquare &&
								isResponsiveHorizontal &&
								"min-[600px]:size-6 min-[600px]:w-auto min-[600px]:h-auto",
							!isSquare && isForcedHorizontal && "size-6 w-auto h-auto",
						)}
						style={
							isSquare
								? { width: resolvedShapeSize, height: resolvedShapeSize }
								: undefined
						}
					>
						<div
							className={cn(
								"absolute inset-0 z-0",
								!isSquare && isResponsiveHorizontal && "min-[600px]:hidden",
								!isSquare && isForcedHorizontal && "hidden",
							)}
						>
							<AnimatePresence initial={false}>
								{selected && (
									<ActivePill
										shape={activeShape}
										width={iconPillWidth}
										height={iconPillHeight}
									/>
								)}
							</AnimatePresence>
							<HoverStateLayer
								shape={activeShape}
								width={iconPillWidth}
								height={iconPillHeight}
							/>
							<RippleLayer
								ripples={ripples}
								onRippleDone={removeRipple}
								shape={activeShape}
								width={iconPillWidth}
								height={iconPillHeight}
							/>
						</div>

						<div className="relative z-10 flex size-6 items-center justify-center text-current">
							<IconContainer selected={selected} badge={badge}>
								{filledIcon}
							</IconContainer>
						</div>
					</div>

					{label && (
						<AnimatePresence mode="popLayout" initial={false}>
							{shouldShowLabel && (
								<m.span
									key="nav-label"
									initial={{ opacity: 0, height: 0, scale: 0.85, y: -2 }}
									animate={{ opacity: 1, height: "auto", scale: 1, y: 0 }}
									exit={{ opacity: 0, height: 0, scale: 0.85, y: -2 }}
									transition={SPRING_TRANSITION_EXPRESSIVE}
									className={cn(
										"z-10 transition-colors duration-200 truncate px-1 inline-block overflow-hidden",
										selected
											? "text-m3-on-surface font-medium"
											: "text-m3-on-surface-variant font-medium",
										"text-[12px] leading-4 tracking-[0.5px]",
									)}
								>
									{label}
								</m.span>
							)}
						</AnimatePresence>
					)}
				</div>
				<TouchTarget />
			</m.button>
		);
	},
);

NavigationBarItemComponent.displayName = "NavigationBarItem";
export const NavigationBarItem = React.memo(NavigationBarItemComponent);

// ─────────────────────────────────────────────────────────────────────────────
// NavigationBar Container
// ─────────────────────────────────────────────────────────────────────────────

const navContainerVariants = cva(
	"flex items-center justify-center select-none transition-transform duration-300 z-50",
	{
		variants: {
			variant: {
				flexible: "bottom-0 left-0 right-0 w-full min-h-16 py-1 pb-safe",
				baseline: "bottom-0 left-0 right-0 w-full min-h-20 py-1 pb-safe",
				xr: "bottom-6 left-1/2 -translate-x-1/2 w-auto max-w-[calc(100%-2rem)] min-[600px]:max-w-fit min-h-20 min-[600px]:min-h-16 rounded-[48px] px-2 py-1",
			},
			position: {
				fixed: "fixed",
				absolute: "absolute",
			},
			elevated: {
				true: "shadow-[0_-1px_3px_rgba(0,0,0,0.1)]",
				false: "shadow-none",
			},
		},
		defaultVariants: {
			variant: "flexible",
			position: "fixed",
			elevated: false,
		},
	},
);

export const NavigationBarComponent = React.forwardRef<
	HTMLElement,
	NavigationBarProps
>(
	(
		{
			variant = "flexible",
			itemLayout,
			shape,
			labelVisibility,
			shapeSize,
			hideOnScroll = false,
			elevated = false,
			fixed = true,
			scrollContainerRef,
			activeIndicatorTransition,
			children,
			itemClassName,
			className,
			style,
		},
		ref,
	) => {
		const [isVisible, setIsVisible] = React.useState(true);
		const lastScrollY = React.useRef(
			typeof window !== "undefined" ? window.scrollY : 0,
		);

		const isSquare = isSquareShape(shape);
		const effectiveShapeSize = shapeSize ?? (isSquare ? 56 : 32);

		const computedMinHeight = React.useMemo(() => {
			const baseHeight = variant === "baseline" ? 80 : 64;
			const requiredHeight =
				itemLayout === "horizontal" || labelVisibility === "unlabeled"
					? effectiveShapeSize + 16
					: effectiveShapeSize + 32;
			return Math.max(baseHeight, requiredHeight);
		}, [variant, itemLayout, labelVisibility, effectiveShapeSize]);

		React.useEffect(() => {
			if (typeof window === "undefined" || !hideOnScroll) {
				setIsVisible(true);
				return;
			}

			// Do not hide if screen reader or reduced motion is active
			const prefersReducedMotion = window.matchMedia(
				"(prefers-reduced-motion: reduce)",
			).matches;
			if (prefersReducedMotion) {
				setIsVisible(true);
				return;
			}

			let ticking = false;

			const handleScroll = () => {
				if (ticking) return;
				ticking = true;
				window.requestAnimationFrame(() => {
					const currentScrollY = scrollContainerRef?.current
						? scrollContainerRef.current.scrollTop
						: window.scrollY;

					if (currentScrollY <= 0 || currentScrollY < lastScrollY.current) {
						setIsVisible(true);
					} else if (currentScrollY > lastScrollY.current) {
						setIsVisible(false);
					}

					lastScrollY.current = currentScrollY;
					ticking = false;
				});
			};

			const target = scrollContainerRef?.current || window;

			target.addEventListener("scroll", handleScroll, { passive: true });
			return () => target.removeEventListener("scroll", handleScroll);
		}, [hideOnScroll, scrollContainerRef]);

		const navBaseClasses = cn(
			navContainerVariants({
				variant,
				elevated,
				position: fixed ? "fixed" : "absolute",
			}),
			variant === "xr"
				? "bg-m3-surface border border-white/5 shadow-xl"
				: "bg-m3-surface-container",
			className,
		);

		return (
			<LazyMotion features={domMax} strict>
				<NavigationBarContext.Provider
					value={{
						variant,
						itemLayout,
						shape,
						labelVisibility,
						shapeSize,
						activeIndicatorTransition,
						itemClassName,
					}}
				>
					<m.nav
						ref={ref}
						role="navigation"
						aria-label="Main navigation"
						className={navBaseClasses}
						style={{ minHeight: computedMinHeight, ...style }}
						initial={false}
						animate={{
							y: isVisible
								? "0%"
								: variant === "xr"
									? "calc(100% + 40px)"
									: "100%",
						}}
						transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
					>
						<div
							role="menubar"
							aria-orientation="horizontal"
							className={cn(
								"flex w-full h-full mx-auto items-center justify-center",
								variant === "xr"
									? "gap-0 min-[600px]:gap-1.5 px-1"
									: "max-w-7xl gap-1.5",
							)}
						>
							{children}
						</div>
					</m.nav>
				</NavigationBarContext.Provider>
			</LazyMotion>
		);
	},
);

NavigationBarComponent.displayName = "NavigationBar";
export const NavigationBar = React.memo(NavigationBarComponent);
